import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { CommandBarButton, CommandBarButtonMenuItem, ListActionButtonProps, TooltipHost } from "sprint-common-web";

import { FileRecordStatus } from "../../../../common/types/FileRecordStatus";
import { AuthorizationWrapper } from "../../../../core/security/AuthorizationWrapper";
import { SprintAccessRight } from "../../../../core/security/SprintAccessRight";
import { useAccessVerifier } from "../../../../core/security/useAccessVerifier";
import { ciCreateFileRecordFormRoute } from "../../../ContentIngestionRoutes";
import { FileRecordQcDoneDialog } from "../../common/components/qc-done/FileRecordQcDoneDialog";
import {
    canFileRecordBeChangedToDestroyed,
    canFileRecordBeChangedToEditing,
    canFileRecordBeChangedToQc,
    canFileRecordBeChangedToRejected,
    canFileRecordBeChangedToWaitingForClientResponse
} from "../../common/utils/validateFileRecordStatus";
import { isFileRecordRemovable } from "../../remove/shouldRemoveButtonBeDisabled";
import { ApproveFileRecordNotificationsListingDialog } from "../buttons/approve-notifications/ApproveFileRecordNotificationsListingDialog";
import { ChangeFileRecordToEditingListingDialog } from "../buttons/change-to-editing/ChangeFileRecordToEditingListingDialog";
import { ChangeFileRecordToQcListingDialog } from "../buttons/change-to-qc/ChangeFileRecordToQcListingDialog";
import { ChangeSingleFileRecordToWaitingForResponseDialog } from "../buttons/change-to-waiting-for-client-response/ChangeSingleFileRecordToWaitingForResponseDialog";
import { DestroyFileRecordListingDialog } from "../buttons/destroy/DestroyFileRecordListingDialog";
import { RemoveFileRecordListingDialog } from "../buttons/remove/RemoveFileRecordListingDialog";
import { FileRecordListingContext } from "../context/FileRecordListingContext";
import { RejectSingleFileRecordDialog } from "../reject/RejectSingleFileRecordsStatusDialog";
import { FileRecordListingItem } from "../types/FileRecordListingItem";

type FileRecordActionsListingButtonProps = ListActionButtonProps<FileRecordListingItem>;

enum FileRecordActions {
    ApproveNotifications,
    ChangeToEditing,
    ChangeToQc,
    ChangeToQcDone,
    ChangeToWaitingForClientResponse,
    Destroy,
    Reject,
    Remove
}

export const FileRecordActionsListingButton: React.VFC<FileRecordActionsListingButtonProps> = ({
    item
}: FileRecordActionsListingButtonProps) => {
    const history = useHistory();

    const [state, setState] = useState<FileRecordActions>();

    const { isAuthorized } = useAccessVerifier();

    const getMenuItems = (): CommandBarButtonMenuItem[] => {
        const items = [
            {
                text: "Create similar",
                onClick: () => history.push(`${ciCreateFileRecordFormRoute.path}?sourceFileRecordId=${item.id}`)
            },
            {
                text: "Approve notifications",
                disabled: item.isNotificationApproved,
                onClick: () => setState(FileRecordActions.ApproveNotifications)
            },
            {
                text: "Status",
                subMenuProps: {
                    items: [
                        {
                            text: "Editing",
                            disabled: isChangeToEditingDisabled(item),
                            onClick: () => setState(FileRecordActions.ChangeToEditing)
                        },
                        {
                            text: "Qc",
                            disabled: isChangeToQcDisabled(item),
                            onClick: () => setState(FileRecordActions.ChangeToQc)
                        },
                        {
                            text: "Qc Done",
                            disabled: isChangeToQcDoneDisabled(item),
                            onClick: () => setState(FileRecordActions.ChangeToQcDone)
                        },
                        {
                            text: "Rejected",
                            disabled: isChangeToRejectedDisabled(item),
                            onClick: () => setState(FileRecordActions.Reject)
                        },
                        {
                            text: "Destroyed",
                            disabled: isChangeToDestroyedDisabled(item),
                            onClick: () => setState(FileRecordActions.Destroy)
                        },
                        {
                            text: "Waiting for client response",
                            disabled: isChangeToWaitingForClientResponseDisabled(item),
                            onClick: () => setState(FileRecordActions.ChangeToWaitingForClientResponse)
                        }
                    ]
                }
            }
        ];

        if (isAuthorized([SprintAccessRight.CiAdmin])) {
            items.push({
                text: "Remove",
                disabled: !isFileRecordRemovable({
                    status: item.status,
                    isNotificationApproved: item.isNotificationApproved
                }),
                onClick: () => setState(FileRecordActions.Remove)
            });
        }

        return items;
    };

    return (
        <AuthorizationWrapper accessRights={[SprintAccessRight.CiReadWrite]}>
            <TooltipHost content="Additional">
                <CommandBarButton
                    icon="MoreVertical"
                    testId="file-record-listing-action-button"
                    menuItems={getMenuItems()}
                />
            </TooltipHost>
            <FileRecordDialogManager status={state} item={item} onClose={() => setState(undefined)} />
        </AuthorizationWrapper>
    );
};

const FileRecordDialogManager = ({
    status,
    item,
    onClose
}: {
    status?: FileRecordActions;
    item: FileRecordListingItem;
    onClose: () => void;
}) => {
    const { updateFileRecordStatus } = useContext(FileRecordListingContext);

    switch (status) {
        case FileRecordActions.ApproveNotifications:
            return <ApproveFileRecordNotificationsListingDialog isOpen itemId={item.id} onClose={onClose} />;
        case FileRecordActions.ChangeToEditing:
            return <ChangeFileRecordToEditingListingDialog isOpen itemId={item.id} onClose={onClose} />;
        case FileRecordActions.ChangeToQc:
            return <ChangeFileRecordToQcListingDialog isOpen itemId={item.id} onClose={onClose} />;
        case FileRecordActions.ChangeToQcDone:
            return (
                <FileRecordQcDoneDialog
                    isOpen
                    itemId={item.id}
                    onClose={onClose}
                    onSubmit={() => updateFileRecordStatus(item.id, FileRecordStatus.QcDone)}
                />
            );
        case FileRecordActions.Destroy:
            return <DestroyFileRecordListingDialog isOpen itemId={item.id} onClose={onClose} />;
        case FileRecordActions.Reject:
            return <RejectSingleFileRecordDialog isOpen itemId={item.id} onClose={onClose} />;
        case FileRecordActions.Remove:
            return <RemoveFileRecordListingDialog isOpen itemId={item.id} onClose={onClose} />;
        case FileRecordActions.ChangeToWaitingForClientResponse:
            return <ChangeSingleFileRecordToWaitingForResponseDialog isOpen fileRecordId={item.id} onClose={onClose} />;
        default:
            return <></>;
    }
};

const isChangeToEditingDisabled = (item: FileRecordListingItem): boolean =>
    !canFileRecordBeChangedToEditing(item.status);

const isChangeToQcDoneDisabled = (item: FileRecordListingItem): boolean =>
    item.status === FileRecordStatus.Destroyed ||
    item.status === FileRecordStatus.Rejected ||
    item.status === FileRecordStatus.QcDone;

const isChangeToQcDisabled = (item: FileRecordListingItem): boolean => !canFileRecordBeChangedToQc(item.status);

const isChangeToDestroyedDisabled = (item: FileRecordListingItem): boolean =>
    !canFileRecordBeChangedToDestroyed(item.status);

const isChangeToRejectedDisabled = (item: FileRecordListingItem): boolean =>
    !canFileRecordBeChangedToRejected(item.status);

const isChangeToWaitingForClientResponseDisabled = (item: FileRecordListingItem): boolean =>
    !canFileRecordBeChangedToWaitingForClientResponse(item.status);
